/* eslint-disable filenames/match-exported */
/* eslint-disable filenames/match-regex */

import * as d3 from 'd3';

import ChartDataDetails from './chartDataDetails';
import ChartTooltip from '@/chart/chartTooltip';

const useSidePadding = true;
const marginTop = 30;
const marginBottom = 25;
const marginLeft = useSidePadding ? 15 : 0;
const marginRight = useSidePadding ? 15 : 0;

export default class BarChart {
  svg = null;
  width = null;
  height = null;

  showYAxisScaleNumbers = true;
  showYAxisScaleLines = true;

  isDarkTheme = false;

  xScaleType = null;
  yScaleType = null;

  xAxisTicks = 'auto';
  // Functions or null
  xAxisFormat = null;

  #grouppedData = null;
  #mergedData = null;
  #dataCollection = [];

  #xScale = null;
  #yScale = null;
  #y2Scale = null;
  #xSubgroup = null;

  #subgroups = [];
  #subgroupColors = {};

  #rectWidth = 14;

  constructor(element, width, height, options = {}) {
    this.width = width;
    this.height = height;

    const defaults = {
      xAxisTicks: 'auto',
      xAxisFormat: null,
      xScaleType: 'scalePoint',
      yScaleType: 'scaleLinear',
      showYAxisScaleNumbers: false,
      showYAxisScaleLines: false,
      isDarkTheme: false,
    };
    options = Object.assign({}, defaults, options);

    this.xAxisTicks = options.xAxisTicks;
    this.xAxisFormat = options.xAxisFormat;
    this.xScaleType = options.xScaleType;
    this.yScaleType = options.yScaleType;
    this.showYAxisScaleNumbers = options.showYAxisScaleNumbers;
    this.showYAxisScaleLines = options.showYAxisScaleLines;
    this.isDarkTheme = options.isDarkTheme;

    this.svg = d3.select(element)
      // .attr("style", 'background:lightblue')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [ 0, 0, width, height ])
      .attr('style', 'max-width: 100%; height: auto; height: intrinsic;');
  }

  // Use this method for GROUPPED BAR chart
  addData(value) {
    this.#dataCollection.push(value);

    // subgroups = [cpc, ctr]
    // groups = [date1, date2, ..]
  }

  // Use this method for SINGLE BAR chart with faked, full height bar behind
  setData(value) {
    const clonedForFullHeightBg = this._cloneChartDataDetails(value);

    const yMax = d3.max(clonedForFullHeightBg.data, d => d.value);
    clonedForFullHeightBg.data.forEach(i => {
      if (typeof i.date !== 'undefined') {
        i.date = new Date(i.date);
      }

      i.value = yMax + Math.ceil(yMax * 0.1);
    });

    this.#dataCollection = [ clonedForFullHeightBg, value ];
  }

  _cloneChartDataDetails(o) {
    const brokenChainObject = JSON.parse(JSON.stringify(o));
    const publicProps = Object.getOwnPropertyNames(brokenChainObject);

    const clonedForFullHeightBg = new ChartDataDetails(brokenChainObject.data, brokenChainObject.xKey, brokenChainObject.yKey);
    publicProps.forEach(prop => {
      if ([ 'data', 'xKey', 'yKey' ].includes(prop)) {
        return;
      }

      clonedForFullHeightBg[prop] = brokenChainObject[prop];
    });

    return clonedForFullHeightBg;
  }

  render() {
    this._renderAxes();

    if (this.#grouppedData != null && Array.isArray(this.#grouppedData) && this.#grouppedData.length > 0) {
      this._renderGrouppedBars(this.#grouppedData, this.#yScale);
    }
    else {
      this._renderBarsFor(this.#dataCollection, this.#yScale);
    }

    this.svg
      .on('pointerenter pointermove', event => this._onPointerMoved(event))
      .on('pointerleave', event => this._onPointerLeft(event));
  }

  _renderAxes() {
    const xValues = [];
    const yValues = [];

    const mergedData = [];
    const grouppedData = {};

    this.#dataCollection.forEach(details => {
      const transformedData = details.data.map(i => ({ x: i[details.xKey], y: i[details.yKey] }));
      mergedData.push( ...transformedData );

      xValues.push( ...details.data.map(i => i[details.xKey]) );
      yValues.push( ...details.data.map(i => i[details.yKey]) );

      // Groupped data
      if (typeof details.group === 'string') {
        details.data.map(i => {
          if (typeof grouppedData[i[details.xKey]] == 'undefined') {
            grouppedData[i[details.xKey]] = {
              group: i[details.xKey],
              color: details.color,
            };
          }

          grouppedData[i[details.xKey]]['color'] = details.color;
          grouppedData[i[details.xKey]][details.group ?? 'value'] = i[details.yKey];
        });
      }
    });

    if (Object.keys(grouppedData).length > 0) {
      this.#grouppedData = Object.values(grouppedData);
      this.#subgroups = [ ...new Set(this.#dataCollection.map(item => item.group)) ];
      this.#subgroupColors = this.#dataCollection.map(item => ({
        group: item.group,
        color: item.color,
      }));
    }

    this.#mergedData = mergedData;

    const division = typeof this.xAxisTicks === 'number'
      ? this.xAxisTicks
      : mergedData.length
    ;
    const rectMaxWidth = Math.floor((this.width - marginLeft - marginRight) / division);
    this.#rectWidth = d3.max([ 1, d3.min([ 14, rectMaxWidth ]) ]);
    const offsetSide = this.#subgroups.length == 0
      ? this.#rectWidth / 2
      : (this.#rectWidth * this.#subgroups.length) / 2
    ;

    const yScaleMax = d3.max(mergedData, d => d.y);
    // let yScaleMaxScaleIncrement = Math.ceil(yScaleMax * 0.1)

    let range = null;
    if (this.xScaleType == 'scaleUtc' || this.xScaleType == 'scaleDate') {
      range = [ offsetSide + marginLeft, this.width - offsetSide - marginRight ];
      this.#xScale = d3[this.xScaleType](d3.extent(mergedData, d => d.x), range);
    }
    else if (this.xScaleType == 'scaleBand' || this.xScaleType == 'scalePoint') {
      range = [ offsetSide + marginLeft, this.width - offsetSide - marginRight ];

      const xKey = this.#dataCollection[0].xKey;
      this.#xScale = d3[this.xScaleType]()
        .domain(this.#dataCollection[0].data.map(d => d[xKey]))
        // .range([marginLeft, this.width - marginRight])
        .range(range)
        .padding(0)
      ;
    }
    else {
      throw new Error('Not implemented');
    }

    this.#xSubgroup = null;
    if (this.#grouppedData !== null && this.#subgroups !== null) {
      this.#xSubgroup = d3.scaleBand()
        .domain(this.#subgroups)
        .range([ 0, (this.#rectWidth * this.#subgroups.length) ])
        .padding(0);
      // .range([0, (this.#rectWidth * this.#subgroups.length) + ((this.#subgroups.length - 1) * 1)])
      // .padding(0)
    }

    this.#yScale = d3[this.yScaleType]([ 0, yScaleMax ], [ this.height - marginBottom, marginTop ]);

    this._renderXAxis();

    if (this.showYAxisScaleNumbers || this.showYAxisScaleLines) {
      this._renderYAxisWithGridLines();
    }
  }

  _renderBarsFor(dataCollection, yScale) {
    const randomHash = this._generateRandomHash(8);

    dataCollection.forEach((details, key) => {
      this.svg.append('g')
        .attr('id', key === 0 ? 'bar-filler' : 'bar-data')
        .selectAll('bar' + key)
        .data(details.data)
        .enter()
        .append('rect')
        .attr('x', d => this.#xScale(d[details.xKey]) - (this.#rectWidth / 2))
        .attr('y', d => yScale(d[details.yKey]))
        .attr('height', d => yScale(0) - yScale(d[details.yKey]))
        .attr('width', this.#rectWidth)
        .attr('rx', this.#rectWidth / 2)
        .attr('clip-path', (d, i) => key === 1 ? `url(#rounded-bar-clip--${randomHash}--${i})` : null)
        .call(r => {
          r.attr('fill', (key === 0 ? details.fill_color : details.color));
        })
      ;

      // Clip path to fix rounded bar's appearance on small height.
      if (key === 0) {
        this.svg.append('defs')
          .selectAll('.rounded-bar-clip')
          .data(details.data)
          .enter()
          .append('clipPath')
          .attr('class', 'rounded-bar-clip')
          .attr('id', (d, i) => `rounded-bar-clip--${randomHash}--${i}`)
          .append('rect')
          .attr('x', d => this.#xScale(d[details.xKey]) - (this.#rectWidth / 2))
          .attr('y', d => yScale(d[details.yKey]))
          .attr('height', d => yScale(0) - yScale(d[details.yKey]))
          .attr('width', this.#rectWidth)
          .attr('rx', this.#rectWidth / 2)
          .call(r => {
            r.attr('fill', (key === 0 ? details.fill_color : details.color));
          })
        ;
      }
    });

    dataCollection.forEach(details => {
      if (details.tooltip instanceof ChartTooltip) {
        details.tooltip.color = details.color;
        details.tooltip.bisect = d3.bisector(d => d[details.xKey]).center;
      }
    });
  }

  _renderGrouppedBars(mergedData, yScale) {
    const thisSubgroups = this.#subgroups;
    const color = d3.scaleOrdinal()
      .domain(this.#subgroupColors.map(g => g.group))
      .range(this.#subgroupColors.map(g => g.color));

    const randomHash = this._generateRandomHash(8);

    this.svg.append('g')
      .selectAll('g')
      // Enter in data = loop group per group
      .data(mergedData)
      .join('g')
      .attr('transform', d => `translate(${this.#xScale(d.group) - this.#rectWidth}, 0)`)
      .selectAll('rect')
      .data(d => thisSubgroups.map(key => ({ key, value: d[key], color: d.color })))
      .join('rect')
      .attr('x', d => this.#xSubgroup(d.key))
      .attr('y', d => yScale(d.value))
      .attr('width', this.#rectWidth)
      .attr('height', d => yScale(0) - yScale(d.value))
      .attr('rx', this.#rectWidth / 2)
      .attr('fill', d => color(d.key))
      .attr('clip-path', (d, i) => `url(#rounded-bar-clip--${randomHash}--${i})`)
    ;

    const yScaleMax = yScale.domain()[1];
    this.svg.append('defs')
      .selectAll('.rounded-bar-clip')
      .data(mergedData)
      .enter()
      .append('clipPath')
      .attr('class', 'rounded-bar-clip')
      .attr('id', (d, i) => `rounded-bar-clip--${randomHash}--${i}`)
      .selectAll('rect')
      .data(d => thisSubgroups.map(key => ({ key, value: d[key], color: d.color })))
      .join('rect')
      .attr('x', d => this.#xSubgroup(d.key))
      .attr('y', () => yScale(yScaleMax))
      .attr('width', this.#rectWidth)
      .attr('height', () => yScale(0) - yScale(yScaleMax))
      .attr('rx', this.#rectWidth / 2)
      .attr('fill', d => color(d.key))
    ;

    this.#dataCollection.forEach(details => {
      if (details.tooltip instanceof ChartTooltip) {
        details.tooltip.color = details.color;
        details.tooltip.bisect = d3.bisector(d => d[details.xKey]).center;
      }
    });
  }

  _renderXAxis() {
    let xAxisBottom = d3.axisBottom(this.#xScale).tickSizeOuter(0);
    if (typeof this.xAxisTicks === 'number') {
      xAxisBottom = xAxisBottom.ticks(this.xAxisTicks);
    }
    else if (Array.isArray(this.xAxisTicks)) {
      xAxisBottom = xAxisBottom.tickValues(this.xAxisTicks);
    }
    else {
      xAxisBottom = xAxisBottom.ticks(this.width / 80);
    }

    if (this.xAxisFormat !== null) {
      xAxisBottom = xAxisBottom.tickFormat(this.xAxisFormat);
    }

    this.svg.append('g')
      .attr('transform', 'translate(0, ' + (this.height - marginBottom) + ')')
      .call(xAxisBottom)
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('.tick line').remove();
        g.selectAll('.tick text')
          .attr('fill', this.isDarkTheme ? '#A3AED0' : '#8B93AE')
          .attr('dy', '0.6em')
          .style('font-size', '9px')
          .style('font-weight', '500')
          .style('cursor', 'default');
      });
  }

  _renderYAxisWithGridLines() {
    let yAxisCallee = d3.axisLeft(this.#yScale).ticks(this.height / 40);
    if (typeof this.yAxisFormat == 'function') {
      yAxisCallee = yAxisCallee.tickFormat(this.yAxisFormat);
    }

    this.svg.append('g')
      .call(yAxisCallee)
      .call(g => {
        g.select('.domain').remove();

        if (this.showYAxisScaleLines) {
          g.selectAll('.tick line')
            .attr('x1', '0')
            .attr('x2', '100%')
            .attr('stroke', this.isDarkTheme ? '#656565' : '#D1D7E4')
            .attr('stroke-dasharray', '6');
        }

        if (this.showYAxisScaleNumbers) {
          g.selectAll('.tick text')
            .attr('fill', '#A3AED0')
            .style('font-size', '9px')
            .style('font-weight', '500')
            .style('cursor', 'default')
            .attr('x', 0);
        }
        else {
          g.selectAll('.tick text').remove();
        }
      })
      .attr('text-anchor', 'start');
  }

  _onPointerMoved(event) {
    // Single data bar chart with faked full height dataset must be override to 1.
    let collectionFakedLength = 1;
    if (this.#grouppedData !== null) {
      collectionFakedLength = this.#dataCollection.length;
    }
    const updateDetailsOnDataCollection = (collection, yScale) => collection.forEach((details, index) => {
      details.updateDynamicDetails(event, this.#xScale, yScale, index, collectionFakedLength, this.#rectWidth);
    });

    updateDetailsOnDataCollection(this.#dataCollection, this.#yScale);
  }

  _onPointerLeft() {
    const hideDetailsOnDataCollection = collection => collection.forEach(details => {
      details.hideOnHoverDetails();
    });

    hideDetailsOnDataCollection(this.#dataCollection);
  }

  _generateRandomHash(length = 4) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }
}
